/**
 * Ready-made staff roles.
 *
 * The 141 permission codes were always there and always enforced; what the app
 * had no way to do was COMPOSE them. With no role editor and no permission
 * catalogue on screen, "a cashier who can also do X" had no smaller expression
 * than handing over Admin — which is how a counter cashier on the live shop
 * ended up holding back-office admin rights.
 *
 * These are installed as ordinary, editable roles (via POST /roles, which
 * find-or-creates and refuses to silently widen a role that already exists).
 * They are a starting point, not a locked system set: a shop is expected to
 * adjust one afterwards.
 *
 * ## Why each bundle is bigger than its obvious domain
 *
 * A route guard only protects the ROUTE. Once a page is open it fires its own
 * fetches, and each of those has its own permission. A bundle that covers only
 * the obvious domain produces a page that loads and then fails piecemeal, which
 * reads as "the system is broken" rather than "you are missing a permission".
 * So each preset below lists what its screens actually call.
 *
 * `currencies.read` is on every one of them for a reason that is easy to miss:
 * the currency registry boots on every signed-in surface, and without it money
 * formatting falls back to CLDR — which gives IQD two decimal places instead of
 * none. Every figure on screen would be wrong by a factor of 100, silently.
 *
 * `settings.read` likewise: the module-toggle store reads it, and on failure it
 * falls back to "everything enabled", so a role without it sees navigation to
 * modules the shop has switched off.
 */

/**
 * Which app a role may sign in from. Only `till` can prove itself — see role_surfaces.
 *
 * Deliberately NARROWER than the API's own `ClientSurface`, which also has
 * `manager`: who opens the manager desktop exe is decided by the owner role and
 * the `manager.access` PERMISSION, not by this list, so it must not be grantable
 * here. Widening this to match would put a checkbox on screen that the login
 * schema then rejects.
 */
export type ClientSurface = 'web' | 'till' | 'rep';

export interface RolePreset {
  /** Stable role code. Also the i18n key: `roles:presets.<code>.name`. */
  code: string;
  /** Fallback English name, used when a client has no translation loaded. */
  defaultName: string;
  permissionCodes: readonly string[];
  /**
   * Apps this role may sign in from. Omitted means "any", which is the same
   * empty-set-means-unrestricted convention `pos_terminal_users` uses.
   */
  surfaces?: readonly ClientSurface[];
  /**
   * The toggleable module this job DEPENDS on. Named explicitly rather than
   * inferred from the permissions, because inferring gets it wrong: an
   * Accountant holds `attachments.read`, and a shop with the files module off
   * still very much wants an accountant. Only set it where the whole role is
   * pointless without the module — a register cashier with POS switched off.
   */
  module?: string;
}

/** On every web preset — see the note above before trimming either of these. */
const WEB_BASE = ['currencies.read', 'settings.read'] as const;

/** Anyone who opens a document or a product needs to read the catalogue behind it. */
const CATALOGUE_READ = ['items.read', 'parties.read', 'warehouses.read', 'stock.read'] as const;

/** Running a warehouse's shelves — the keeper's job, and the base of a branch manager's. */
const KEEPER = [
  ...WEB_BASE,
  'items.read',
  'warehouses.read',
  'stock.read', 'stock.adjust',
  'stock.transfer.read', 'stock.transfer.write',
  // What the shelves hold and are worth, for the keeper's own branches only.
  'stock.report',
  // The stock import screen is gated on items.write, not on stock.*, and
  // POST /inventory-import needs both it and stock.adjust.
  'items.write',
  'attachments.read',
  // The manager exe's stock counts, labels and offline catalogue — the
  // keeper's side of it; backups and restore stay with the owner.
  'manager.access',
] as const;

export const ROLE_PRESETS: readonly RolePreset[] = [
  {
    code: 'accountant',
    defaultName: 'Accountant',
    permissionCodes: [
      ...WEB_BASE,
      ...CATALOGUE_READ,
      'currencies.convert',
      'gl.read', 'gl.post', 'gl.reverse',
      'periods.read', 'periods.write',
      'accounts.read', 'accounts.write',
      'payments.read', 'payments.write', 'payments.post', 'payments.receive',
      // Posting rights without drafting rights: an accountant approves what the
      // clerks wrote, rather than authoring sales and purchases themselves.
      'invoices.read', 'invoices.post',
      'bills.read', 'bills.post',
      'exchange_rates.read', 'exchange_rates.write',
      'reports.read',
      // The statements themselves — the accountant's job, and the one report
      // pair a counter role must not have.
      'reports.financial',
      'stock.report',
      'expenses.read', 'expenses.write',
      'audit_logs.read',
      'attachments.read',
      // Treasury transfers and vouchers have no permissions of their own — they
      // ride on gl.read/gl.post, granted above.
    ],
  },
  {
    code: 'sales_clerk',
    defaultName: 'Sales clerk',
    permissionCodes: [
      ...WEB_BASE,
      ...CATALOGUE_READ,
      'currencies.convert',
      'sales_orders.read', 'sales_orders.write',
      'invoices.read', 'invoices.write', 'invoices.post',
      'quotations.read', 'quotations.write',
      'parties.write',
      'payments.read',
      'reports.read',
      'attachments.read', 'attachments.write',
    ],
  },
  {
    code: 'purchasing_clerk',
    defaultName: 'Purchasing clerk',
    permissionCodes: [
      ...WEB_BASE,
      ...CATALOGUE_READ,
      'currencies.convert',
      'purchase_orders.read', 'purchase_orders.write',
      'bills.read', 'bills.write', 'bills.post',
      'parties.write',
      'payments.read',
      'attachments.read', 'attachments.write',
    ],
  },
  {
    code: 'pos_cashier',
    defaultName: 'Cashier — register',
    permissionCodes: [
      ...WEB_BASE,
      ...CATALOGUE_READ,
      'pos.read', 'pos.sell', 'pos.shift.open', 'pos.shift.close',
      // The register's customer picker can create a walk-in mid-sale.
      'parties.write',
      'attachments.read',
      // NOT items.write: the quick-add-product dialog writes to the shop's
      // catalogue, which is a decision per person rather than a default.
    ],
    surfaces: ['web'],
    module: 'pos',
  },
  {
    code: 'shift_supervisor',
    defaultName: 'Shift supervisor',
    permissionCodes: [
      ...WEB_BASE,
      ...CATALOGUE_READ,
      'pos.read', 'pos.sell', 'pos.shift.open', 'pos.shift.close',
      'parties.write',
      'attachments.read',
      // What separates a supervisor from a cashier: undoing a sale and reading
      // the cross-shift Z-report.
      'pos.refund', 'pos.zreport',
      // And the two rights of the keeper who sells: back-dating a sale and
      // re-opening a finished one of their own branch to correct it.
      'pos.sale.date', 'pos.sale.edit',
    ],
    surfaces: ['web'],
    module: 'pos',
  },
  {
    code: 'till_cashier',
    defaultName: 'Cashier — till (.exe)',
    // Deliberately tiny, and deliberately mandob.*: the desktop till reaches the
    // server ONLY through /mandob/me/* (catalogue sync, sale push, shift push,
    // returns, coupon validation, creating a customer). Giving it pos.* instead
    // would produce a till that signs in and can do nothing at all.
    permissionCodes: ['mandob.read', 'mandob.sell'],
    // No mandob.collect: settling a customer's debt in the field is the rep's
    // job, not the counter's.
    surfaces: ['till'],
    module: 'mandob',
  },
  {
    code: 'warehouse_keeper',
    defaultName: 'Warehouse keeper',
    permissionCodes: KEEPER,
  },
  {
    code: 'branch_manager',
    defaultName: 'Branch manager',
    // A keeper who also answers for the branch's money going out. Give the
    // person their branch (Users → branches) and everything below is theirs
    // alone: stock, the stock report and expenses are all read and written
    // through that scope. Deliberately NOT gl.* — the ledger is whole-company,
    // so it would show them every other branch's books.
    permissionCodes: [
      ...KEEPER,
      'expenses.read', 'expenses.write',
      // The expense form's two account pickers (the chart of accounts, not
      // balances) and the receipt photo.
      'accounts.read',
      'attachments.write',
    ],
  },
  {
    code: 'attendance_kiosk',
    defaultName: 'Attendance kiosk',
    // A DEVICE, not a person: the tablet by the door. It punches people in and
    // out on their code + PIN and can do nothing else — no board, no employee
    // list, no names beyond the one who just punched.
    permissionCodes: [...WEB_BASE, 'hr.time.kiosk'],
    surfaces: ['web'],
    module: 'hr',
  },
  {
    code: 'field_rep',
    defaultName: 'Field rep',
    permissionCodes: ['mandob.read', 'mandob.sell', 'mandob.collect'],
    surfaces: ['rep'],
    module: 'mandob',
  },
];

export function rolePresetByCode(code: string): RolePreset | undefined {
  return ROLE_PRESETS.find((p) => p.code === code);
}
